import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SarIcon from "../icons/SarIcon";

import {
  useApplyForLoanMutation,
  useGetLoanCollateralPreviewQuery,
  useGetMyInvestmentsQuery,
} from "../../redux/api/authApi";
import { mapMyInvestmentRow } from "./myInvestmentsMapper";
import noImgPlaceholder from "../../Assets/no-img-placeholder.png";

/**
 * Nominal annual interest rate, in percent. Single source of truth: it drives the
 * repayment estimate shown at review and is sent as `interestRate` on submit.
 */
const ANNUAL_INTEREST_RATE_PCT = 8;

const STEPS = [
  { key: "details", label: "Loan Details" },
  { key: "financial", label: "Financial Info" },
  { key: "review", label: "Review" },
];

/** `value` is what the API expects for `purpose`. */
const PURPOSE_OPTIONS = [
  { value: "investment-in-other-assets", label: "Investment in Other Assets" },
  { value: "business-expansion", label: "Business Expansion" },
  { value: "personal-use", label: "Personal Use" },
  { value: "debt-refinancing", label: "Debt Refinancing" },
  { value: "other", label: "Other" },
];

const TERM_OPTIONS = [6, 12, 24, 36, 48, 60];

/** `value` is what the API expects for `employmentStatus`. */
const EMPLOYMENT_OPTIONS = [
  { value: "full-time-employed", label: "Full-time Employed" },
  { value: "self-employed", label: "Self-employed" },
  { value: "part-time-employed", label: "Part-time Employed" },
  { value: "business-owner", label: "Business Owner" },
  { value: "retired", label: "Retired" },
  { value: "other", label: "Other" },
];

const optionLabel = (options, value) =>
  options.find((o) => o.value === value)?.label || value || "—";

/**
 * Placeholder credit bureau result. Replace with the SIMAH lookup once that
 * endpoint exists — the panel is rendered from this shape.
 */
const CREDIT_REPORT = {
  score: 782,
  max: 900,
  rating: "Excellent",
  updated: "Nov 2024",
  note: "Your excellent credit score qualifies you for preferential interest rates",
};

const NEXT_STEPS = [
  "Your application will be reviewed by our team within 24-48 hours",
  "If approved, you'll receive a DocuSign link to review and sign the loan agreement",
  "Final approval and fund disbursement will occur after document signing",
  "Your collateral asset will be locked during the loan period",
];

const digitsOnly = (v) => String(v).replace(/\D/g, "");

const formatSar = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return `SAR ${num.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

/** Monthly payment for an amortized loan; falls back to straight division at 0%. */
function monthlyPayment(principal, months, annualRatePct = ANNUAL_INTEREST_RATE_PCT) {
  if (!principal || !months) return 0;
  const i = annualRatePct / 100 / 12;
  if (i === 0) return principal / months;
  return (principal * i) / (1 - Math.pow(1 + i, -months));
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 36,
    borderRadius: 1.25,
    bgcolor: "background.paper",
    fontSize: 14,
    "& fieldset": { borderColor: (theme) => theme.palette.border.light },
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: (theme) => theme.palette.text.secondary,
    opacity: 1,
  },
  // Disabled: greyed surface, faded text and a not-allowed cursor.
  "& .MuiOutlinedInput-root.Mui-disabled": {
    bgcolor: "background.subtle",
    cursor: "not-allowed",
    "& fieldset": { borderColor: (theme) => theme.palette.border.light },
    "& .MuiOutlinedInput-input": {
      cursor: "not-allowed",
      WebkitTextFillColor: (theme) => theme.palette.text.arrow,
    },
    "& .MuiOutlinedInput-input::placeholder": {
      color: (theme) => theme.palette.text.arrow,
      opacity: 1,
    },
    "& .MuiInputAdornment-root": { opacity: 0.5 },
  },
};

const FieldLabel = ({ children, sx }) => (
  <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.strong", mb: 1, ...sx }}>
    {children}
  </Typography>
);

const HelperText = ({ children }) => (
  <Typography sx={{ fontSize: 12, color: "text.muted", mt: 1 }}>{children}</Typography>
);

const WizardStepper = ({ activeStep }) => (
  <Stack direction="row" alignItems="center" justifyContent="center" sx={{ py: 2 }}>
    {STEPS.map((step, index) => {
      const done = index < activeStep;
      const current = index === activeStep;
      const reached = done || current;
      return (
        <React.Fragment key={step.key}>
          <Stack alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) =>
                  reached ? theme.palette.accent.cyan.deep : theme.palette.border.light,
                color: (theme) =>
                  reached ? theme.palette.common.white : theme.palette.text.muted,
                boxShadow: (theme) =>
                  reached ? theme.palette.shadow.cardHover : "none",
              }}
            >
              {done ? (
                <CheckIcon sx={{ fontSize: 24 }} />
              ) : (
                <Typography sx={{ fontSize: 16 }}>{index + 1}</Typography>
              )}
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                whiteSpace: "nowrap",
                color: reached ? "text.primary" : "text.muted",
              }}
            >
              {step.label}
            </Typography>
          </Stack>
          {index < STEPS.length - 1 && (
            <Box
              sx={{
                width: 64,
                height: 4,
                mx: 1,
                mb: 3,
                flexShrink: 0,
                bgcolor: (theme) =>
                  done ? theme.palette.accent.cyan.deep : theme.palette.border.light,
              }}
            />
          )}
        </React.Fragment>
      );
    })}
  </Stack>
);

const CollateralCard = ({ asset, selected, onSelect }) => (
  <Box
    role="radio"
    aria-checked={selected}
    tabIndex={0}
    onClick={() => onSelect(asset)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(asset);
      }
    }}
    sx={{
      cursor: "pointer",
      borderRadius: 1.5,
      overflow: "hidden",
      border: (theme) =>
        `2px solid ${
          selected ? theme.palette.accent.cyan.deep : theme.palette.border.light
        }`,
      boxShadow: (theme) =>
        selected
          ? `0 0 0 2px ${theme.palette.accent.cyan.border}, ${theme.palette.shadow.cardHover}`
          : "none",
      transition: "border-color 120ms, box-shadow 120ms",
      "&:hover": {
        borderColor: (theme) =>
          selected ? theme.palette.accent.cyan.deep : theme.palette.border.medium,
      },
    }}
  >
    <Box sx={{ position: "relative", height: 128 }}>
      <Box
        component="img"
        src={asset.image || noImgPlaceholder}
        alt=""
        onError={(e) => {
          e.currentTarget.src = noImgPlaceholder;
        }}
        sx={{ width: "100%", height: 128, objectFit: "cover", display: "block" }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 100%)",
        }}
      />
      {asset.category && (
        <Chip
          label={asset.category}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            height: 20,
            borderRadius: 1.25,
            fontSize: 12,
            fontWeight: 600,
            bgcolor: "rgba(255,255,255,0.9)",
            color: "text.neutral",
          }}
        />
      )}
      {selected && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 24,
            height: 24,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: (theme) => theme.palette.accent.cyan.deep,
          }}
        >
          <CheckIcon sx={{ fontSize: 16, color: "common.white" }} />
        </Box>
      )}
    </Box>
    <Box sx={{ p: 1.5, bgcolor: "background.paper" }}>
      <Typography sx={{ fontSize: 14, color: "text.primary", lineHeight: "20px" }}>
        {asset.label}
      </Typography>
      <Typography
        sx={{
          fontSize: 16,
          mt: 0.5,
          color: (theme) => theme.palette.accent.cyan.deep,
        }}
      >
        {formatSar(asset.value)}
      </Typography>
    </Box>
  </Box>
);

const SummaryRow = ({ label, value, capitalize }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mt: 1.5 }}>
    <Typography sx={{ fontSize: 14, color: "text.muted" }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: 14,
        color: "text.primary",
        textAlign: "right",
        textTransform: capitalize ? "capitalize" : "none",
      }}
    >
      {value}
    </Typography>
  </Stack>
);

const InvestorLoanApplicationModal = ({ open, onClose, onSubmit }) => {
  const [step, setStep] = React.useState(0);
  const [collateral, setCollateral] = React.useState(null);
  const [amount, setAmount] = React.useState("");
  const [purpose, setPurpose] = React.useState("");
  const [term, setTerm] = React.useState(12);
  const [annualIncome, setAnnualIncome] = React.useState("");
  const [employment, setEmployment] = React.useState("");
  const [additionalInfo, setAdditionalInfo] = React.useState("");
  const [submitError, setSubmitError] = React.useState("");

  const { data, isFetching, isError } = useGetMyInvestmentsQuery(
    { page: 1, limit: 50 },
    { skip: !open }
  );
  // `currentData` (not `data`) so a failed or in-flight lookup never shows the
  // previously selected asset's figures.
  const {
    currentData: previewData,
    isFetching: previewLoading,
    isError: previewError,
    refetch: refetchPreview,
  } = useGetLoanCollateralPreviewQuery(collateral?.id, {
    skip: !open || !collateral?.id,
  });
  const [applyForLoan, { isLoading: submitting }] = useApplyForLoanMutation();

  const assets = React.useMemo(() => {
    const rows = Array.isArray(data?.data) ? data.data : [];
    return rows
      .map(mapMyInvestmentRow)
      .filter(Boolean)
      .map((m) => ({
        id: m.assetId,
        label: `${m.assetName}${m.tokensOwned ? ` (${m.tokensOwned} Tokens)` : ""}`,
        name: m.assetName,
        category: m.categoryName || m.propertyType || m.category || "",
        image: m.mainImageUrl,
        value: Number(m.currentValue ?? m.totalAmount) || 0,
      }));
  }, [data]);

  // Reset the wizard whenever it is reopened.
  React.useEffect(() => {
    if (!open) return;
    setStep(0);
    setCollateral(null);
    setAmount("");
    setPurpose("");
    setTerm(12);
    setAnnualIncome("");
    setEmployment("");
    setAdditionalInfo("");
    setSubmitError("");
  }, [open]);

  // Preselect the first holding once the list arrives.
  React.useEffect(() => {
    if (!open) return;
    setCollateral((prev) => prev ?? assets[0] ?? null);
  }, [open, assets]);

  // Collateral value, borrowing cap and LTV all come from the server.
  const preview = previewData?.data;
  const collateralLabel = preview
    ? `${preview.assetName}${
        preview.tokensOwned != null ? ` (${preview.tokensOwned} Tokens)` : ""
      }`
    : collateral?.label || "—";
  const collateralValue = Number(preview?.collateralValue) || 0;
  const maxLoan = Number(preview?.maxLoanAmount) || 0;
  const ltvPercent = Number(preview?.ltvPercent) || 0;

  const amountNum = Number(amount) || 0;
  const overMax = maxLoan > 0 && amountNum > maxLoan;

  const monthly = monthlyPayment(amountNum, term);
  const totalRepayment = monthly * term;
  const totalInterest = totalRepayment - amountNum;
  const ltvRatio = collateralValue ? (amountNum / collateralValue) * 100 : 0;

  /** The repayment estimate only appears once a usable amount has been entered. */
  const showEstimate = Boolean(preview) && amountNum > 0 && !overMax;

  const step1Valid = Boolean(preview) && amountNum > 0 && !overMax && purpose && term;
  const step2Valid = Number(annualIncome) > 0 && Boolean(employment);

  const handleSubmit = async () => {
    setSubmitError("");
    try {
      const result = await applyForLoan({
        assetId: collateral?.id,
        amount: amountNum,
        interestRate: ANNUAL_INTEREST_RATE_PCT,
        termMonths: term,
        purpose,
        annualIncome: Number(annualIncome) || 0,
        employmentStatus: employment,
      }).unwrap();
      onSubmit?.(result);
      onClose?.();
    } catch (err) {
      setSubmitError(
        err?.data?.message || "Could not submit your application. Please try again."
      );
    }
  };

  const footerButtonSx = {
    height: 36,
    borderRadius: 1.25,
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
  };

  const primaryButtonSx = {
    ...footerButtonSx,
    px: 2,
    color: "common.white",
    bgcolor: (theme) => theme.palette.accent.cyan.deep,
    boxShadow: "none",
    "&:hover": {
      bgcolor: (theme) => theme.palette.accent.cyan.textDeep,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      opacity: 0.5,
      color: (theme) => theme.palette.common.white,
      bgcolor: (theme) => theme.palette.accent.cyan.deep,
    },
  };

  const secondaryButtonSx = {
    ...footerButtonSx,
    px: 2.125,
    bgcolor: "background.paper",
    borderColor: "border.light",
    color: "text.strong",
    "&:hover": { borderColor: "border.medium", bgcolor: "background.subtle" },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
        },
      }}
    >
      <Box sx={{ position: "relative", p: 3 }}>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: "absolute", top: 12, right: 12, color: "text.muted", opacity: 0.7 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>

        {/* Title */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 4 }}>
          <SarIcon sx={{ fontSize: 24, color: (theme) => theme.palette.accent.cyan.deep }} />
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.strong" }}>
            Apply for Asset-Backed Loan
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 1 }}>
          Unlock liquidity from your tokenized assets without selling. Complete the
          application below.
        </Typography>

        <WizardStepper activeStep={step} />

        {/* Step 1 — Loan Details */}
        {step === 0 && (
          <Box>
            <FieldLabel sx={{ mb: 0 }}>Select Collateral Asset *</FieldLabel>
            <HelperText>
              Choose from your tokenized assets to use as loan collateral
            </HelperText>

            {isFetching ? (
              <Stack alignItems="center" sx={{ py: 5 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : isError ? (
              <Typography sx={{ fontSize: 14, color: "error.main", py: 3 }}>
                Could not load your assets. Please try again.
              </Typography>
            ) : assets.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: "text.muted", py: 3 }}>
                You don't hold any tokenized assets yet, so there is nothing to pledge as
                collateral.
              </Typography>
            ) : (
              <Box
                role="radiogroup"
                aria-label="Collateral asset"
                sx={{
                  mt: 1.5,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                }}
              >
                {assets.map((asset) => (
                  <CollateralCard
                    key={asset.id}
                    asset={asset}
                    selected={collateral?.id === asset.id}
                    onSelect={setCollateral}
                  />
                ))}
              </Box>
            )}

            {collateral && (
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2.125,
                  borderRadius: 1.5,
                  background: (theme) => theme.palette.accent.cyan.bgLight,
                  border: (theme) => `1px solid ${theme.palette.accent.cyan.border}`,
                }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography sx={{ fontSize: 14, color: "text.muted" }}>
                      Selected Collateral
                    </Typography>
                    <Typography sx={{ fontSize: 16, color: "text.primary" }}>
                      {collateralLabel}
                    </Typography>
                  </Box>
                  {previewLoading ? (
                    <CircularProgress size={18} sx={{ flexShrink: 0, mt: 0.5 }} />
                  ) : (
                    preview && (
                      <Chip
                        label={`Value: ${formatSar(collateralValue)}`}
                        size="small"
                        sx={{
                          flexShrink: 0,
                          height: 22,
                          borderRadius: 1.25,
                          fontSize: 12,
                          fontWeight: 600,
                          bgcolor: (theme) => theme.palette.badges.cyan.bg,
                          color: (theme) => theme.palette.badges.cyan.text,
                          border: (theme) =>
                            `1px solid ${theme.palette.badges.cyan.border}`,
                        }}
                      />
                    )
                  )}
                </Stack>
                {preview && (
                  <Typography sx={{ fontSize: 12, color: "text.muted", mt: 1 }}>
                    {`Maximum loan amount: ${formatSar(maxLoan)} (${ltvPercent}% LTV)`}
                  </Typography>
                )}

                {previewError && !previewLoading && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 1 }}
                  >
                    <Typography sx={{ fontSize: 12, color: "error.main" }}>
                      Couldn't load the collateral details for this asset, so it can't be
                      used as collateral right now.
                    </Typography>
                    <Button
                      onClick={refetchPreview}
                      sx={{
                        minWidth: 0,
                        p: 0,
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: "none",
                        textDecoration: "underline",
                        color: (theme) => theme.palette.accent.cyan.deep,
                        "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                      }}
                    >
                      Retry
                    </Button>
                    <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                      or select a different asset.
                    </Typography>
                  </Stack>
                )}
              </Paper>
            )}

            <Box sx={{ mt: 2 }}>
              <FieldLabel>Loan Amount (SAR) *</FieldLabel>
              <TextField
                fullWidth
                value={amount}
                onChange={(e) => setAmount(digitsOnly(e.target.value))}
                placeholder="Enter amount"
                disabled={previewError}
                error={overMax}
                helperText={
                  overMax
                    ? `Exceeds the maximum of ${formatSar(maxLoan)}`
                    : showEstimate
                      ? `LTV Ratio: ${ltvRatio.toFixed(1)}%`
                      : undefined
                }
                FormHelperTextProps={{
                  sx: {
                    ml: 0,
                    fontSize: 12,
                    color: overMax ? "error.main" : "success.main",
                  },
                }}
                inputProps={{ inputMode: "numeric" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 700, color: "text.faint" }}
                      >
                        SAR
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <FieldLabel>Loan Purpose *</FieldLabel>
              <TextField
                select
                fullWidth
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) =>
                    v ? (
                      PURPOSE_OPTIONS.find((o) => o.value === v)?.label
                    ) : (
                      <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                        Select purpose
                      </Typography>
                    ),
                }}
                sx={fieldSx}
              >
                {PURPOSE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: 14 }}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ mt: 2 }}>
              <FieldLabel>Loan Term (Months) *</FieldLabel>
              <TextField
                select
                fullWidth
                value={term}
                onChange={(e) => setTerm(Number(e.target.value))}
                sx={fieldSx}
              >
                {TERM_OPTIONS.map((m) => (
                  <MenuItem key={m} value={m} sx={{ fontSize: 14 }}>
                    {`${m} months`}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {showEstimate && (
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: "background.subtle",
                  border: (theme) => `1px solid ${theme.palette.border.light}`,
                }}
              >
                <Typography sx={{ fontSize: 14, color: "text.muted" }}>
                  Estimated Monthly Payment
                </Typography>
                <Typography
                  sx={{ fontSize: 30, lineHeight: "40px", color: "text.strong", mt: 0.5 }}
                >
                  {formatSar(monthly.toFixed(2))}
                </Typography>

                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: (theme) => `1px solid ${theme.palette.border.light}`,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 14, color: "text.muted" }}>
                      Total Interest
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "text.strong", mt: 0.5 }}>
                      {formatSar(totalInterest.toFixed(2))}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 14, color: "text.muted" }}>
                      Total Repayment
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "text.strong", mt: 0.5 }}>
                      {formatSar(totalRepayment.toFixed(2))}
                    </Typography>
                  </Box>
                </Stack>

                <Typography sx={{ fontSize: 12, color: "text.muted", mt: 1.5 }}>
                  {`* Based on ${ANNUAL_INTEREST_RATE_PCT}% APR. Final rate subject to approval.`}
                </Typography>
              </Paper>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 4 }}>
              <Button variant="outlined" onClick={onClose} sx={secondaryButtonSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!step1Valid}
                onClick={() => setStep(1)}
                sx={primaryButtonSx}
              >
                Continue
              </Button>
            </Stack>
          </Box>
        )}

        {/* Step 2 — Financial Info */}
        {step === 1 && (
          <Box>
            <FieldLabel>Annual Income (SAR) *</FieldLabel>
            <TextField
              fullWidth
              value={annualIncome}
              onChange={(e) => setAnnualIncome(digitsOnly(e.target.value))}
              placeholder="Enter annual income"
              inputProps={{ inputMode: "numeric" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.faint" }}>
                      SAR
                    </Typography>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
            <HelperText>Gross annual income before taxes</HelperText>

            <Box sx={{ mt: 2 }}>
              <FieldLabel>Employment Status *</FieldLabel>
              <TextField
                select
                fullWidth
                value={employment}
                onChange={(e) => setEmployment(e.target.value)}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) =>
                    v ? (
                      EMPLOYMENT_OPTIONS.find((o) => o.value === v)?.label
                    ) : (
                      <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                        Select employment status
                      </Typography>
                    ),
                }}
                sx={fieldSx}
              >
                {EMPLOYMENT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: 14 }}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Credit score verification */}
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 2.25,
                borderRadius: 1.5,
                background: (theme) => theme.palette.credit.bg,
                border: (theme) => `2px solid ${theme.palette.credit.border}`,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon
                    sx={{ fontSize: 20, color: (theme) => theme.palette.credit.score }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: 14, color: "text.primary" }}>
                      Credit Score Verification
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                      Powered by SIMAH
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{
                    flexShrink: 0,
                    px: 1.625,
                    py: 0.875,
                    borderRadius: 0.5,
                    bgcolor: "background.paper",
                    border: (theme) => `1px solid ${theme.palette.border.light}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: (theme) => theme.palette.credit.logo,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "common.white" }}>
                      S
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.slate" }}>
                    SIMAH
                  </Typography>
                </Stack>
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  mt: 1.5,
                  p: 1.625,
                  borderRadius: 1.5,
                  bgcolor: "background.paper",
                  border: (theme) => `1px solid ${theme.palette.credit.cardBorder}`,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                      Your Credit Score
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: 30,
                          lineHeight: "36px",
                          color: (theme) => theme.palette.credit.score,
                        }}
                      >
                        {CREDIT_REPORT.score}
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: "text.muted" }}>
                        {`/ ${CREDIT_REPORT.max}`}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.5}>
                    <Chip
                      label={CREDIT_REPORT.rating}
                      size="small"
                      sx={{
                        height: 22,
                        borderRadius: 1.25,
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: (theme) => theme.palette.badges.success.bg,
                        color: (theme) => theme.palette.badges.success.text,
                        border: (theme) => `1px solid ${theme.palette.badges.success.border}`,
                      }}
                    />
                    <Typography sx={{ fontSize: 12, color: "text.muted" }}>
                      {`Updated: ${CREDIT_REPORT.updated}`}
                    </Typography>
                  </Stack>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={(CREDIT_REPORT.score / CREDIT_REPORT.max) * 100}
                  sx={{
                    mt: 1.5,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: (theme) => theme.palette.border.light,
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      background: (theme) =>
                        `linear-gradient(90deg, ${theme.palette.credit.barFrom} 0%, ${theme.palette.credit.barTo} 100%)`,
                    },
                  }}
                />
                <Typography sx={{ fontSize: 12, color: "text.muted", mt: 1 }}>
                  {CREDIT_REPORT.note}
                </Typography>
              </Paper>
            </Paper>

            <Box sx={{ mt: 2 }}>
              <FieldLabel>Additional Information (Optional)</FieldLabel>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder="Any additional information to support your application..."
                sx={{
                  ...fieldSx,
                  "& .MuiOutlinedInput-root": {
                    ...fieldSx["& .MuiOutlinedInput-root"],
                    height: "auto",
                    minHeight: 64,
                    alignItems: "flex-start",
                  },
                }}
              />
              <HelperText>
                Share any relevant details about your financial situation or loan purpose
              </HelperText>
            </Box>

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
              <Button variant="outlined" onClick={() => setStep(0)} sx={secondaryButtonSx}>
                Back
              </Button>
              <Button
                variant="contained"
                disabled={!step2Valid}
                onClick={() => setStep(2)}
                sx={primaryButtonSx}
              >
                Continue to Review
              </Button>
            </Stack>
          </Box>
        )}

        {/* Step 3 — Review */}
        {step === 2 && (
          <Box>
            <Paper
              elevation={0}
              sx={{
                p: 2.125,
                borderRadius: 1.5,
                background: (theme) => theme.palette.accent.cyan.tile,
                border: (theme) => `1px solid ${theme.palette.accent.cyan.border}`,
              }}
            >
              <Typography sx={{ fontSize: 16, color: "text.primary" }}>
                Application Summary
              </Typography>

              <SummaryRow label="Loan Amount:" value={formatSar(amountNum)} />
              <SummaryRow
                label="Purpose:"
                value={optionLabel(PURPOSE_OPTIONS, purpose)}
              />
              <SummaryRow label="Loan Term:" value={`${term} months`} />
              <SummaryRow label="Collateral:" value={collateralLabel} />
              <SummaryRow label="LTV Ratio:" value={`${ltvRatio.toFixed(1)}%`} />

              <Box
                sx={{
                  mt: 1.5,
                  pt: 1.625,
                  borderTop: (theme) => `1px solid ${theme.palette.accent.cyan.border}`,
                }}
              >
                <SummaryRow label="Monthly Payment:" value={formatSar(monthly.toFixed(2))} />
                <SummaryRow label="Total Interest:" value={formatSar(totalInterest.toFixed(2))} />
                <SummaryRow
                  label="Total Repayment:"
                  value={formatSar(totalRepayment.toFixed(2))}
                />
              </Box>

              <Box
                sx={{
                  mt: 1.5,
                  pt: 1.625,
                  borderTop: (theme) => `1px solid ${theme.palette.accent.cyan.border}`,
                }}
              >
                <SummaryRow label="Annual Income:" value={formatSar(annualIncome)} />
                <SummaryRow
                  label="Employment:"
                  value={optionLabel(EMPLOYMENT_OPTIONS, employment)}
                />
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 2.125,
                borderRadius: 1.5,
                bgcolor: (theme) => theme.palette.note.bg,
                border: (theme) => `1px solid ${theme.palette.note.border}`,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <ErrorOutlineIcon
                  sx={{
                    fontSize: 20,
                    mt: 0.25,
                    flexShrink: 0,
                    color: (theme) => theme.palette.note.icon,
                  }}
                />
                <Box>
                  <Typography sx={{ fontSize: 14, color: "text.primary" }}>
                    Next Steps
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ m: 0, mt: 0.5, pl: 2.125, listStyle: "disc" }}
                  >
                    {NEXT_STEPS.map((item) => (
                      <Box
                        component="li"
                        key={item}
                        sx={{
                          fontSize: 12,
                          lineHeight: "16px",
                          color: "text.neutral",
                          mt: 0.5,
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Stack>
            </Paper>

            {submitError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSubmitError("")}>
                {submitError}
              </Alert>
            )}

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                disabled={submitting}
                onClick={() => setStep(1)}
                sx={secondaryButtonSx}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={
                  submitting ? (
                    <CircularProgress size={14} sx={{ color: "common.white" }} />
                  ) : null
                }
                sx={{
                  ...footerButtonSx,
                  px: 2,
                  color: "common.white",
                  background: (theme) => theme.palette.gradients.cyanBlue,
                  boxShadow: "none",
                  "&:hover": {
                    background: (theme) => theme.palette.gradients.cyanBlueHover,
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    color: (theme) => theme.palette.common.white,
                    background: (theme) => theme.palette.gradients.cyanBlue,
                  },
                }}
              >
                {submitting ? "Submitting…" : "Submit Application"}
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default InvestorLoanApplicationModal;
